import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { BreadcrumbSegment } from '@/config/routes';

export type { BreadcrumbSegment };

export interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  className?: string;
}

/**
 * Chevron-separated breadcrumb. Earlier segments are muted (link if `to`
 * provided, plain text otherwise); the final segment is bold and never linked.
 * Visual style mirrors the ERP shell's Breadcrumbs component.
 */
export function Breadcrumb({ segments, className }: BreadcrumbProps) {
  if (segments.length === 0) return null;
  return (
    <nav
      aria-label="Breadcrumb"
      className={`flex items-center gap-4 text-xs text-neutral-500 ${className ?? ''}`}
    >
      <ol className="flex items-center gap-4">
        {segments.map((seg, i) => {
          const isLast = i === segments.length - 1;
          return (
            <li key={`${seg.label}-${i}`} className="flex items-center gap-4">
              {i > 0 && (
                <ChevronRight className="h-12 w-12 text-neutral-300" aria-hidden="true" />
              )}
              {isLast || !seg.to ? (
                <span
                  className={
                    isLast
                      ? 'font-semibold text-neutral-800'
                      : 'text-neutral-500'
                  }
                  aria-current={isLast ? 'page' : undefined}
                >
                  {seg.label}
                </span>
              ) : (
                <Link
                  to={seg.to}
                  className="text-neutral-500 transition-colors hover:text-primary-600 motion-reduce:transition-none"
                >
                  {seg.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
